import { useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Users, CheckSquare, Plus, Search, Pencil, Trash2,
  MoreHorizontal, Power, PowerOff,
  ChevronDown, CalendarDays,
} from 'lucide-react';

import { TaskPlannerView } from '@/components/planner/TaskPlannerView';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { PriorityBadge } from '@/components/shared/PriorityBadge';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { EmptyState } from '@/components/shared/EmptyState';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { TeamMembersDialog } from '@/components/teams/TeamMembersDialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useToast } from '@/hooks/use-toast';
import { useAuthStore } from '@/stores/auth.store';
import { UnifiedTaskDetailsDialog } from '@/components/tasks/UnifiedTaskDetailsDialog';
import { teamService, type TeamMember } from '@/services/team.service';
import { taskService, type Task, PHASE_STATUSES, PRIORITIES } from '@/services/task.service';

// ---------------------------------------------------------------------------
// TeamDetailPage
// ---------------------------------------------------------------------------
export function TeamDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const backTab = (location.state as any)?.fromTab || 'teams-list';
  const backUrl = `/work?tab=${backTab}`;
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const user = useAuthStore((s) => s.user);

  // ── Dialog state ──
  const [membersOpen, setMembersOpen] = useState(false);
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const [deleteTaskId, setDeleteTaskId] = useState<string | null>(null);
  const [editTaskId, setEditTaskId] = useState<string | null>(null);
  const [viewTaskId, setViewTaskId] = useState<string | null>(null);
  const [editTeamOpen, setEditTeamOpen] = useState(false);
  const [deleteTeamOpen, setDeleteTeamOpen] = useState(false);

  // ── Edit team state ──
  const [editTeamName, setEditTeamName] = useState('');
  const [editTeamDesc, setEditTeamDesc] = useState('');

  // ── Planner state ──
  const [selectedPlannerMemberId, setSelectedPlannerMemberId] = useState<string | null>(null);

  // ── Filter state ──
  const [search, setSearch] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [phaseFilter, setPhaseFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');

  // ── Create task state ──
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskPriority, setTaskPriority] = useState('medium');
  const [taskAssignee, setTaskAssignee] = useState('');
  const [taskStartDate, setTaskStartDate] = useState('');
  const [taskEndDate, setTaskEndDate] = useState('');
  const [taskEstHours, setTaskEstHours] = useState('');

  // ── Edit task state ──
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPriority, setEditPriority] = useState('medium');
  const [editAssignee, setEditAssignee] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editEstHours, setEditEstHours] = useState('');

  // ── Queries ──
  const { data: team, isLoading } = useQuery({
    queryKey: ['team', id],
    queryFn: () => teamService.get(id!).then((r) => r.data.data),
    enabled: !!id,
  });

  const { data: tasksData } = useQuery({
    queryKey: ['team-tasks', id],
    queryFn: () => taskService.list({ team_id: id!, limit: 100 }).then((r) => r.data),
    enabled: !!id,
  });

  // ── Permissions ──
  const perms = team?.current_user_permissions;
  const canEdit = perms?.can_edit ?? false;
  const canDelete = perms?.can_delete ?? false;
  const canManageMembers = perms?.can_manage_members ?? false;
  const canChangeRoles = perms?.can_change_roles ?? false;
  const canCreateTasks = perms?.can_create_tasks ?? false;
  const canEditAnyTask = perms?.can_edit_any_task ?? false;

  // Old system pattern: owners/leaders can manage all tasks, members can manage tasks they created
  const canManageTask = (task: Task) => {
    if (canEditAnyTask) return true;
    return task.creator_id === user?.id;
  };

  // ── Filtered tasks ──
  const allTasks: Task[] = tasksData?.data ?? [];

  const filteredTasks = useMemo(() => {
    return allTasks.filter((task) => {
      if (search && !task.title.toLowerCase().includes(search.toLowerCase()) && !task.reference_id?.toLowerCase().includes(search.toLowerCase())) return false;
      if (!showCompleted && task.phase_status === 'done') return false;
      if (phaseFilter !== 'all' && task.phase_status !== phaseFilter) return false;
      if (priorityFilter !== 'all' && task.priority !== priorityFilter) return false;
      if (assigneeFilter !== 'all' && task.assignee_id !== assigneeFilter) return false;
      return true;
    });
  }, [allTasks, search, showCompleted, phaseFilter, priorityFilter, assigneeFilter]);

  // Unique assignees for filter
  const assigneeOptions = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const t of allTasks) {
      if (t.assignee_id && t.assignee) {
        map.set(t.assignee_id, { id: t.assignee_id, name: t.assignee.full_name });
      }
    }
    return Array.from(map.values());
  }, [allTasks]);

  // ── Planner: tasks for selected member ──
  const memberPlannerTasks = useMemo(() => {
    if (!selectedPlannerMemberId) return [];
    return allTasks.filter((task) => task.assignee_id === selectedPlannerMemberId);
  }, [allTasks, selectedPlannerMemberId]);

  // ── Invalidation helper ──
  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['team', id] });
    queryClient.invalidateQueries({ queryKey: ['team-tasks', id] });
    queryClient.invalidateQueries({ queryKey: ['work-teams'] });
  }

  // ── Create task mutation ──
  const createTaskMut = useMutation({
    mutationFn: () => taskService.create({
      title: taskTitle,
      description: taskDescription || undefined,
      priority: taskPriority,
      assignee_id: taskAssignee,
      start_date: taskStartDate,
      end_date: taskEndDate,
      estimated_hours: taskEstHours ? parseFloat(taskEstHours) : undefined,
      task_type: 'general',
      team_id: id!,
    }),
    onSuccess: () => {
      invalidate();
      toast({ title: 'Task created' });
      setCreateTaskOpen(false);
      resetCreateForm();
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed to create task', variant: 'destructive' }),
  });

  // ── Edit task mutation ──
  const editTaskMut = useMutation({
    mutationFn: () => taskService.update(editTaskId!, {
      title: editTitle,
      description: editDescription || undefined,
      priority: editPriority,
      assignee_id: editAssignee || undefined,
      start_date: editStartDate || undefined,
      end_date: editEndDate || undefined,
      estimated_hours: editEstHours ? parseFloat(editEstHours) : undefined,
    }),
    onSuccess: () => {
      invalidate();
      toast({ title: 'Task updated' });
      setEditTaskId(null);
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed to update task', variant: 'destructive' }),
  });

  // ── Delete task mutation ──
  const deleteTaskMut = useMutation({
    mutationFn: () => taskService.delete(deleteTaskId!),
    onSuccess: () => {
      invalidate();
      toast({ title: 'Task deleted' });
      setDeleteTaskId(null);
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed to delete task', variant: 'destructive' }),
  });

  // ── Toggle active mutation ──
  const toggleActiveMut = useMutation({
    mutationFn: () => teamService.update(id!, { is_active: !team?.is_active }),
    onSuccess: () => {
      invalidate();
      toast({ title: team?.is_active ? 'Team deactivated' : 'Team activated' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  // ── Edit team mutation ──
  const editTeamMut = useMutation({
    mutationFn: () => teamService.update(id!, { name: editTeamName.trim(), description: editTeamDesc.trim() || undefined }),
    onSuccess: () => {
      invalidate();
      toast({ title: 'Team updated' });
      setEditTeamOpen(false);
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  // ── Delete team mutation ──
  const deleteTeamMut = useMutation({
    mutationFn: () => teamService.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-teams'] });
      toast({ title: 'Team deleted' });
      navigate(backUrl);
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed to delete team', variant: 'destructive' }),
  });

  function resetCreateForm() {
    setTaskTitle(''); setTaskDescription(''); setTaskPriority('medium');
    setTaskAssignee(''); setTaskStartDate(''); setTaskEndDate(''); setTaskEstHours('');
  }

  function openEditTeam() {
    if (!team) return;
    setEditTeamName(team.name);
    setEditTeamDesc(team.description ?? '');
    setEditTeamOpen(true);
  }

  function openEditTask(task: Task) {
    setEditTaskId(task.id);
    setEditTitle(task.title);
    setEditDescription(task.description ?? '');
    setEditPriority(task.priority);
    setEditAssignee(task.assignee_id ?? '');
    setEditStartDate(task.start_date ?? '');
    setEditEndDate(task.end_date ?? '');
    setEditEstHours(task.estimated_hours != null ? String(task.estimated_hours) : '');
  }

  // ── Loading / Not Found ──
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-6 w-48" />
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 3 }, (_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  if (!team) {
    return <EmptyState title="Team not found" description="This team may have been deleted." />;
  }

  const members: TeamMember[] = team.members ?? [];

  return (
    <div className="space-y-6 min-w-0">
      {/* ── Header ── */}
      <div>
        <button
          type="button"
          onClick={() => navigate(backUrl)}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Work Planning
        </button>

        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono text-xs bg-muted text-muted-foreground px-2 py-1 rounded">{team.reference_id}</span>
              <h1 className="text-2xl font-bold tracking-tight">{team.name}</h1>
              {!team.is_active && <Badge variant="secondary">Inactive</Badge>}
            </div>
            <p className="text-muted-foreground">{team.description || 'No description'}</p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {canManageMembers && (
              <Button variant="outline" size="sm" onClick={() => setMembersOpen(true)}>
                <Users className="mr-1 h-4 w-4" /> Manage Members
              </Button>
            )}
            {canEdit && (
              <Button variant="outline" size="sm" onClick={openEditTeam}>
                <Pencil className="mr-1 h-4 w-4" /> Edit
              </Button>
            )}
            {canEdit && (
              <Button variant="outline" size="sm" onClick={() => toggleActiveMut.mutate()} disabled={toggleActiveMut.isPending}>
                {team.is_active ? <><PowerOff className="mr-1 h-4 w-4" /> Deactivate</> : <><Power className="mr-1 h-4 w-4" /> Activate</>}
              </Button>
            )}
            {canDelete && (
              <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => setDeleteTeamOpen(true)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ── Members Section (collapsible, matching old system) ── */}
      <Card>
        <Collapsible defaultOpen={false}>
          <CardHeader className="pb-3">
            <CollapsibleTrigger className="flex items-center justify-between w-full group">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Team Members
                </CardTitle>
                <CardDescription>
                  {members.length} member{members.length !== 1 ? 's' : ''}
                </CardDescription>
              </div>
              <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {members.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50">
                    <div className="flex items-center gap-3">
                      <UserAvatar name={member.user?.full_name ?? 'Unknown'} avatarUrl={member.user?.avatar_url} size="sm" />
                      <div>
                        <p className="text-sm font-medium">{member.user?.full_name ?? 'Unknown'}</p>
                        <p className="text-xs text-muted-foreground">{member.user?.email}</p>
                      </div>
                    </div>
                    <Badge variant={member.role === 'owner' ? 'default' : 'outline'}>{member.role}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* ── Team Tasks Planner (collapsible, matching old system) ── */}
      <Card className="overflow-hidden w-full min-w-0">
        <Collapsible defaultOpen={false}>
          <CardHeader className="pb-3">
            <CollapsibleTrigger className="flex items-center justify-between w-full group">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CalendarDays className="h-5 w-5" />
                  Team Tasks Planner
                </CardTitle>
                <CardDescription>
                  View team member workload on a calendar timeline
                </CardDescription>
              </div>
              <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="pt-0 space-y-4">
              {/* Member selector */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">Select member:</span>
                <Select value={selectedPlannerMemberId ?? ''} onValueChange={(v) => setSelectedPlannerMemberId(v || null)}>
                  <SelectTrigger className="w-[250px]">
                    <SelectValue placeholder="Choose team member" />
                  </SelectTrigger>
                  <SelectContent>
                    {members.map((member) => (
                      <SelectItem key={member.user?.id ?? member.id} value={member.user?.id ?? member.id}>
                        <div className="flex items-center gap-2">
                          <UserAvatar name={member.user?.full_name ?? 'Unknown'} avatarUrl={member.user?.avatar_url} size="xs" />
                          <span>{member.user?.full_name ?? 'Unknown'}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Planner content — Gantt chart */}
              {selectedPlannerMemberId ? (
                memberPlannerTasks.length > 0 ? (
                  <TaskPlannerView
                    tasks={memberPlannerTasks}
                    onTaskClick={(taskId) => setViewTaskId(taskId)}
                  />
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <CalendarDays className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No tasks assigned to this member</p>
                  </div>
                )
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CalendarDays className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Select a team member to view their task planner</p>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* ── Tasks Section ── */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CheckSquare className="h-5 w-5" />
                Team Tasks
              </CardTitle>
              <CardDescription>
                {filteredTasks.length === allTasks.length
                  ? `${allTasks.length} task${allTasks.length !== 1 ? 's' : ''}`
                  : `${filteredTasks.length} of ${allTasks.length} tasks`}
              </CardDescription>
            </div>
            {canCreateTasks && (
              <Button size="sm" onClick={() => setCreateTaskOpen(true)}>
                <Plus className="mr-1 h-4 w-4" /> Create Task
              </Button>
            )}
          </div>

          {/* Filters */}
          {allTasks.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 mt-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search tasks by title, description, or ID..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" />
              </div>
              <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
                <SelectTrigger className="w-[150px]"><SelectValue placeholder="Assignee" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Assignees</SelectItem>
                  {assigneeOptions.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={phaseFilter} onValueChange={setPhaseFilter}>
                <SelectTrigger className="w-[140px]"><SelectValue placeholder="Phase" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Phases</SelectItem>
                  {PHASE_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-[130px]"><SelectValue placeholder="Priority" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Switch checked={showCompleted} onCheckedChange={setShowCompleted} id="show-completed" />
                <Label htmlFor="show-completed" className="text-sm cursor-pointer whitespace-nowrap">Show completed</Label>
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {allTasks.length === 0 ? (
            <EmptyState icon={CheckSquare} title="No tasks yet" description="Create your first task to get started" />
          ) : filteredTasks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No tasks match the current filters</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 pr-4 font-medium">Ref</th>
                    <th className="pb-2 pr-4 font-medium">Title</th>
                    <th className="pb-2 pr-4 font-medium">Status</th>
                    <th className="pb-2 pr-4 font-medium">Priority</th>
                    <th className="pb-2 pr-4 font-medium">Assignee</th>
                    <th className="pb-2 pr-4 font-medium">Due Date</th>
                    <th className="pb-2 font-medium w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTasks.map((task) => (
                    <tr key={task.id} className="border-b last:border-0 hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => setViewTaskId(task.id)}>
                      <td className="py-3 pr-4">
                        <span className="font-mono text-xs text-muted-foreground">{task.reference_id}</span>
                      </td>
                      <td className="py-3 pr-4">
                        <span className="font-medium truncate block max-w-[300px]">{task.title}</span>
                      </td>
                      <td className="py-3 pr-4">
                        <StatusBadge status={task.phase_status} />
                      </td>
                      <td className="py-3 pr-4">
                        <PriorityBadge priority={task.priority} />
                      </td>
                      <td className="py-3 pr-4">
                        {task.assignee ? (
                          <div className="flex items-center gap-2">
                            <UserAvatar name={task.assignee.full_name} avatarUrl={task.assignee.avatar_url} size="xs" />
                            <span className="text-sm truncate max-w-[120px]">{task.assignee.full_name}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Unassigned</span>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        {task.end_date ? (
                          <span className="text-xs text-muted-foreground">
                            {new Date(task.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="py-3" onClick={(e) => e.stopPropagation()}>
                        {canManageTask(task) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditTask(task)}>
                                <Pencil className="mr-2 h-4 w-4" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem className="text-destructive" onClick={() => setDeleteTaskId(task.id)}>
                                <Trash2 className="mr-2 h-4 w-4" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Members Dialog ── */}
      <TeamMembersDialog
        open={membersOpen}
        onOpenChange={setMembersOpen}
        teamId={id!}
        teamName={team.name}
        canManage={canManageMembers}
        canChangeRoles={canChangeRoles}
      />

      {/* ── Task Details Dialog (click to view) ── */}
      <UnifiedTaskDetailsDialog
        open={!!viewTaskId}
        onOpenChange={(v) => !v && setViewTaskId(null)}
        taskId={viewTaskId}
        canEditOverride={canEditAnyTask}
        canDeleteOverride={canEditAnyTask}
        onDeleted={invalidate}
        members={members.map((m) => ({ id: m.user?.id ?? m.id, name: m.user?.full_name ?? 'Unknown' }))}
      />

      {/* ── Edit Team Dialog ── */}
      <Dialog open={editTeamOpen} onOpenChange={setEditTeamOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Team</DialogTitle>
            <DialogDescription>Update team information</DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); if (editTeamName.trim()) editTeamMut.mutate(); }} className="space-y-4">
            <div className="space-y-2">
              <Label>Team Name *</Label>
              <Input value={editTeamName} onChange={(e) => setEditTeamName(e.target.value)} placeholder="Team name" required />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={editTeamDesc}
                onChange={(e) => setEditTeamDesc(e.target.value)}
                rows={3}
                placeholder="Team description"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditTeamOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={editTeamMut.isPending || !editTeamName.trim()}>
                {editTeamMut.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Delete Team Confirm ── */}
      <ConfirmDialog
        open={deleteTeamOpen}
        onOpenChange={setDeleteTeamOpen}
        title="Delete Team"
        description={`Are you sure you want to delete "${team.name}"? All tasks in this team will also be deleted. This action cannot be undone.`}
        confirmLabel="Delete Team"
        variant="destructive"
        isLoading={deleteTeamMut.isPending}
        onConfirm={() => deleteTeamMut.mutate()}
      />

      {/* ── Create Task Dialog ── */}
      <Dialog open={createTaskOpen} onOpenChange={setCreateTaskOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Create Task</DialogTitle>
            <DialogDescription>Add a new task to this team.</DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); createTaskMut.mutate(); }} className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} placeholder="Task title" required />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={taskDescription}
                onChange={(e) => setTaskDescription(e.target.value)}
                rows={3}
                placeholder="Optional description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={taskPriority} onValueChange={setTaskPriority}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Assignee</Label>
                <Select value={taskAssignee} onValueChange={setTaskAssignee}>
                  <SelectTrigger><SelectValue placeholder="Select assignee" /></SelectTrigger>
                  <SelectContent>
                    {members.map((m) => (
                      <SelectItem key={m.user?.id ?? m.id} value={m.user?.id ?? m.id}>{m.user?.full_name ?? 'Unknown'}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input type="date" value={taskStartDate} onChange={(e) => setTaskStartDate(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label>End Date *</Label>
                <Input type="date" value={taskEndDate} onChange={(e) => setTaskEndDate(e.target.value)} required />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Estimated Hours</Label>
              <Input type="number" step="0.5" min="0" value={taskEstHours} onChange={(e) => setTaskEstHours(e.target.value)} placeholder="e.g. 4" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateTaskOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={createTaskMut.isPending || !taskTitle.trim() || !taskStartDate || !taskEndDate || !taskAssignee}>
                {createTaskMut.isPending ? 'Creating...' : 'Create Task'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Edit Task Dialog ── */}
      <Dialog open={!!editTaskId} onOpenChange={(v) => { if (!v) setEditTaskId(null); }}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
            <DialogDescription>Update the task details.</DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); editTaskMut.mutate(); }} className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} placeholder="Task title" required />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={editPriority} onValueChange={setEditPriority}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Assignee</Label>
                <Select value={editAssignee} onValueChange={setEditAssignee}>
                  <SelectTrigger><SelectValue placeholder="Select assignee" /></SelectTrigger>
                  <SelectContent>
                    {members.map((m) => (
                      <SelectItem key={m.user?.id ?? m.id} value={m.user?.id ?? m.id}>{m.user?.full_name ?? 'Unknown'}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={editStartDate} onChange={(e) => setEditStartDate(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input type="date" value={editEndDate} onChange={(e) => setEditEndDate(e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Estimated Hours</Label>
              <Input type="number" step="0.5" min="0" value={editEstHours} onChange={(e) => setEditEstHours(e.target.value)} placeholder="e.g. 4" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditTaskId(null)}>Cancel</Button>
              <Button type="submit" disabled={editTaskMut.isPending || !editTitle.trim()}>
                {editTaskMut.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Delete Task Confirm ── */}
      <ConfirmDialog
        open={!!deleteTaskId}
        onOpenChange={() => setDeleteTaskId(null)}
        title="Delete Task"
        description="Are you sure you want to delete this task? This action cannot be undone."
        confirmLabel="Delete"
        variant="destructive"
        isLoading={deleteTaskMut.isPending}
        onConfirm={() => deleteTaskMut.mutate()}
      />
    </div>
  );
}
